import { database, DB_PREFIX, cacheGet, cacheSet, invalidateCache, CACHE_TTL, } from "./config";
import {
    ref, get, set, update, query, orderByChild,
    equalTo, push, onValue, remove
} from "firebase/database";
import { updateOrganization } from "./organization";

// Hiring Manager Profile Functions
export const createHiringManagerProfile = async (profileData) => {
    if (!database) {
        console.error("Firebase not initialized - hiring manager profile not created");
        throw new Error("Firebase not initialized");
    }

    try {
        console.log("Creating hiring manager profile with data:", profileData);

        const profilesRef = ref(database, `${DB_PREFIX}hiringManagerProfiles`);
        const newProfileRef = push(profilesRef);

        const dataToSave = {
            ...profileData,
            status: profileData.status || 'pending_verification',
            createdAt: Date.now(),
            updatedAt: Date.now()
        };

        await set(newProfileRef, dataToSave);
        console.log("Hiring manager profile created successfully with key:", newProfileRef.key);
        return newProfileRef.key;
    } catch (error) {
        console.error("Error creating hiring manager profile:", error);
        throw error;
    }
};

export const getHiringManagerProfile = async (email) => {
    if (!database || !email) return null;

    const cacheKey = `hmProfile:${email.toLowerCase()}`;
    const cached = cacheGet(cacheKey);
    if (cached !== undefined) return cached;

    try {
        let foundProfile = null;
        const profilesRef = ref(database, `${DB_PREFIX}hiringManagerProfiles`);

        try {
            // Use indexed query instead of downloading all profiles
            const q = query(profilesRef, orderByChild('workEmail'), equalTo(email));
            const snapshot = await get(q);

            if (snapshot.exists()) {
                snapshot.forEach((childSnapshot) => {
                    foundProfile = { id: childSnapshot.key, ...childSnapshot.val() };
                });
            }
        } catch (indexError) {
            // Fallback: full scan if index not defined
            console.warn('getHiringManagerProfile: index unavailable, using full scan, error: ', indexError);
            const snapshot = await new Promise((resolve, reject) => {
                onValue(profilesRef, resolve, reject, { onlyOnce: true });
            });
            const emailLower = email.toLowerCase();
            snapshot.forEach((childSnapshot) => {
                const profile = childSnapshot.val();
                if (profile.workEmail?.toLowerCase() === emailLower) {
                    foundProfile = { id: childSnapshot.key, ...profile };
                }
            });
        }

        cacheSet(cacheKey, foundProfile, CACHE_TTL.hiringManagerProfile);
        return foundProfile;
    } catch (error) {
        console.error("Error getting hiring manager profile:", error);
        return null;
    }
};

export const updateHiringManagerProfile = async (profileId, updates) => {
    if (!database) {
        console.warn("Firebase not initialized");
        return null;
    }

    // Invalidate hiring manager profile cache
    invalidateCache('hmProfile:');

    try {
        const profileRef = ref(database, `${DB_PREFIX}hiringManagerProfiles/${profileId}`);
        await update(profileRef, {
            ...updates,
            updatedAt: Date.now()
        });
        return true;
    } catch (error) {
        console.error("Error updating hiring manager profile:", error);
        throw error;
    }
};

/**
 * Upgrade a hiring manager's subscription plan
 * When upgrading, credits are reset to the new plan's monthly credit limit (not carried over)
 *
 * @param {string} profileId - The hiring manager profile ID (email key)
 * @param {Object} newPlan - The new plan details
 * @param {string} newPlan.planId - Plan ID (free, starter, growth, scale)
 * @param {string} newPlan.planName - Plan display name
 * @param {number} newPlan.monthlyCredits - Monthly credit allowance
 * @param {number} newPlan.seats - Number of seats included
 * @param {string} newPlan.billingCycle - 'monthly' or 'yearly'
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const upgradeHiringManagerPlan = async (profileId, newPlan) => {
    if (!database || !profileId) {
        console.warn("Firebase not initialized or missing profileId");
        return { success: false, error: "Invalid parameters" };
    }

    try {
        const profileRef = ref(database, `${DB_PREFIX}hiringManagerProfiles/${profileId}`);
        const snapshot = await get(profileRef);

        if (!snapshot.exists()) {
            return { success: false, error: "Profile not found" };
        }

        const profile = snapshot.val();
        const now = new Date();
        const billingPeriodEnd = new Date(now);
        billingPeriodEnd.setMonth(billingPeriodEnd.getMonth() + (newPlan.billingCycle === 'yearly' ? 12 : 1));

        // When upgrading, credits reset to the new plan's monthly credit limit
        // Credits are NOT carried over from the previous plan
        const updates = {
            'planDetails/planId': newPlan.planId,
            'planDetails/planName': newPlan.planName,
            'planDetails/monthlyCredits': newPlan.monthlyCredits,
            'planDetails/creditsRemaining': newPlan.monthlyCredits, // Reset to new plan's credits
            'planDetails/creditsUsed': 0, // Reset used credits
            'planDetails/seats': newPlan.seats,
            'planDetails/billingCycle': newPlan.billingCycle,
            'planDetails/billingPeriodStart': now.toISOString(),
            'planDetails/billingPeriodEnd': billingPeriodEnd.toISOString(),
            'planDetails/nextRenewalDate': billingPeriodEnd.toISOString(),
            'planDetails/subscriptionStatus': 'active',
            'planDetails/upgradedAt': now.toISOString(),
            'planDetails/previousPlan': profile.planDetails?.planId || 'free',
            'updatedAt': Date.now()
        };

        await update(profileRef, updates);

        // Also update the organization's credit balance if this is an org owner
        if (profile.organizationId && profile.role === 'owner') {
            const orgRef = ref(database, `${DB_PREFIX}organizations/${profile.organizationId}`);
            await update(orgRef, {
                plan: newPlan.planId,
                creditBalance: newPlan.monthlyCredits,
                seats: newPlan.seats,
                updatedAt: Date.now()
            });
        }

        console.log(`Plan upgraded to ${newPlan.planName} for ${profileId}. Credits reset to ${newPlan.monthlyCredits}`);
        return { success: true };
    } catch (error) {
        console.error("Error upgrading plan:", error);
        return { success: false, error: error.message };
    }
};

/**
 * Delete a hiring manager account and optionally all team members
 * Only account owner can delete the entire account
 * Team members can only delete their own profile (removed from team)
 */
export const deleteHiringManagerAccount = async (email) => {
    if (!database || !email) {
        return { success: false, error: "Invalid parameters" };
    }

    try {
        // Get the user's profile
        const profile = await getHiringManagerProfile(email);
        if (!profile) {
            return { success: false, error: "Profile not found" };
        }

        const isOwner = profile.role === 'owner';
        const organizationId = profile.organizationId;

        if (isOwner && organizationId) {
            // Owner is deleting - delete entire organization and all team members
            console.log(`Owner ${email} deleting account. Removing organization ${organizationId} and all team members.`);

            // Get all team member profiles in this organization
            const profilesRef = ref(database, `${DB_PREFIX}hiringManagerProfiles`);
            const snapshot = await get(profilesRef);

            const profilesToDelete = [];
            const memberEmailsToDelete = [];
            if (snapshot.exists()) {
                snapshot.forEach((childSnapshot) => {
                    const memberProfile = childSnapshot.val();
                    if (memberProfile.organizationId === organizationId) {
                        profilesToDelete.push(childSnapshot.key);
                        if (memberProfile.workEmail) memberEmailsToDelete.push(memberProfile.workEmail);
                    }
                });
            }

            // Delete all team member profiles
            for (const profileId of profilesToDelete) {
                await remove(ref(database, `${DB_PREFIX}hiringManagerProfiles/${profileId}`));
                console.log(`Deleted team member profile: ${profileId}`);
            }

            // Delete Firebase Auth accounts for all team members
            for (const memberEmail of memberEmailsToDelete) {
                try {
                    await fetch('/.netlify/functions/delete-auth-user', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ email: memberEmail }),
                    });
                } catch (e) {
                    console.warn(`Could not delete auth account for ${memberEmail}:`, e.message);
                }
            }

            // Delete all pending invites for this organization
            const invitesRef = ref(database, `${DB_PREFIX}teamInvites`);
            const invitesSnapshot = await get(invitesRef);
            if (invitesSnapshot.exists()) {
                invitesSnapshot.forEach((childSnapshot) => {
                    const invite = childSnapshot.val();
                    if (invite.organizationId === organizationId) {
                        remove(ref(database, `${DB_PREFIX}teamInvites/${childSnapshot.key}`));
                    }
                });
            }

            // Delete all jobs for this organization
            const jobsRef = ref(database, `${DB_PREFIX}jobs`);
            const jobsSnapshot = await get(jobsRef);
            if (jobsSnapshot.exists()) {
                jobsSnapshot.forEach((childSnapshot) => {
                    const job = childSnapshot.val();
                    if (job.organizationId === organizationId) {
                        remove(ref(database, `${DB_PREFIX}jobs/${childSnapshot.key}`));
                    }
                });
            }

            // Delete the organization itself
            await remove(ref(database, `${DB_PREFIX}organizations/${organizationId}`));
            console.log(`Deleted organization: ${organizationId}`);

            return { success: true, message: `Account and ${profilesToDelete.length} team member(s) deleted` };
        } else {
            // Non-owner member deleting their own profile
            console.log(`Team member ${email} deleting their own profile.`);

            await remove(ref(database, `${DB_PREFIX}hiringManagerProfiles/${profile.id}`));

            // Delete Firebase Auth account
            try {
                await fetch('/.netlify/functions/delete-auth-user', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email }),
                });
            } catch (e) {
                console.warn(`Could not delete auth account for ${email}:`, e.message);
            }

            return { success: true, message: "Your profile has been removed from the team" };
        }
    } catch (error) {
        console.error("Error deleting hiring manager account:", error);
        return { success: false, error: error.message };
    }
};

/**
 * Toggle block status for a hiring manager account
 * Blocks or unblocks the entire organization when blocking an owner
 */
export const toggleHiringManagerBlockStatus = async (profileId, currentBlockedStatus) => {
    if (!database || !profileId) {
        console.warn("Firebase not initialized or missing profileId");
        return { success: false, error: "Invalid parameters" };
    }

    try {
        const profileRef = ref(database, `${DB_PREFIX}hiringManagerProfiles/${profileId}`);
        const snapshot = await get(profileRef);

        if (!snapshot.exists()) {
            return { success: false, error: "Profile not found" };
        }

        const profile = snapshot.val();
        const newBlockedStatus = !currentBlockedStatus;

        // Update the profile's blocked status
        await update(profileRef, {
            blocked: newBlockedStatus,
            blockedAt: newBlockedStatus ? new Date().toISOString() : null
        });

        // If this is an owner and we're blocking, also block the organization
        if (profile.role === 'owner' && profile.organizationId) {
            const orgRef = ref(database, `${DB_PREFIX}organizations/${profile.organizationId}`);
            await update(orgRef, {
                blocked: newBlockedStatus,
                blockedAt: newBlockedStatus ? new Date().toISOString() : null
            });
        }

        console.log(`Hiring manager ${newBlockedStatus ? 'blocked' : 'unblocked'}: ${profileId}`);
        return { success: true, blocked: newBlockedStatus };
    } catch (error) {
        console.error("Error toggling hiring manager block status:", error);
        return { success: false, error: error.message };
    }
};

/**
 * Sync shared organization profile fields to all team members
 * Called when owner/admin updates profile info that should be shared across the team
 * Only syncs: companyName, companyWebsite, companySize
 * Does NOT sync: fullName, jobTitle, workEmail, phoneNumber, hiringFor (these are personal per user)
 */
export const syncProfileToTeamMembers = async (organizationId, sharedFields) => {
    if (!database || !organizationId) {
        console.warn("Firebase not initialized or missing organizationId");
        return { success: false, error: "Invalid parameters" };
    }

    try {
        // Get all team members in this organization
        const profilesRef = ref(database, `${DB_PREFIX}hiringManagerProfiles`);
        const snapshot = await get(profilesRef);

        if (!snapshot.exists()) {
            return { success: true, updatedCount: 0 };
        }

        const updates = {};
        let updatedCount = 0;

        snapshot.forEach((childSnapshot) => {
            const profile = childSnapshot.val();
            // Only update profiles that belong to this organization and are active
            if (profile.organizationId === organizationId && profile.seatStatus === 'active') {
                const profileId = childSnapshot.key;
                // Update only the shared organization fields (company info only)
                // Personal fields like fullName, jobTitle, workEmail, phoneNumber, hiringFor are NOT synced
                if (sharedFields.companyName !== undefined) {
                    updates[`${DB_PREFIX}hiringManagerProfiles/${profileId}/companyName`] = sharedFields.companyName;
                }
                if (sharedFields.companyWebsite !== undefined) {
                    updates[`${DB_PREFIX}hiringManagerProfiles/${profileId}/companyWebsite`] = sharedFields.companyWebsite;
                }
                if (sharedFields.companySize !== undefined) {
                    updates[`${DB_PREFIX}hiringManagerProfiles/${profileId}/companySize`] = sharedFields.companySize;
                }
                updates[`${DB_PREFIX}hiringManagerProfiles/${profileId}/updatedAt`] = Date.now();
                updatedCount++;
            }
        });

        if (Object.keys(updates).length > 0) {
            // Use multi-path update for atomic update across all profiles
            const rootRef = ref(database);
            await update(rootRef, updates);
            console.log(`Synced company profile fields to ${updatedCount} team members`);
        }

        return { success: true, updatedCount };
    } catch (error) {
        console.error("Error syncing profile to team members:", error);
        return { success: false, error: error.message };
    }
};
export const getAllHiringManagerProfiles = async () => {


    try {
        const profilesRef = ref(database, `${DB_PREFIX}hiringManagerProfiles`);
        const snapshot = await get(profilesRef);

        const profiles = [];
        if (snapshot.exists()) {
            snapshot.forEach((childSnapshot) => {
                profiles.push({
                    id: childSnapshot.key,
                    ...childSnapshot.val()
                });
            });
        }
        return profiles;
    } catch (error) {
        console.error("Error getting all hiring manager profiles:", error);
        return [];
    }
};





/**
 * Fix business account seats (one-time fix for accounts created before seat fix)
 * Updates both the profile's planDetails.seats and organization's maxSeats to correct values
 */
export const fixBusinessAccountSeats = async (email, correctSeats = 2) => {
    if (!database) {
        console.warn("Firebase not initialized");
        return { success: false, error: "Firebase not initialized" };
    }

    try {
        // Get the hiring manager profile
        const profile = await getHiringManagerProfile(email);
        if (!profile) {
            return { success: false, error: "Profile not found" };
        }

        // Update profile planDetails.seats
        await updateHiringManagerProfile(profile.id, {
            'planDetails/seats': correctSeats
        });

        // Update organization maxSeats if exists
        if (profile.organizationId) {
            await updateOrganization(profile.organizationId, {
                maxSeats: correctSeats
            });
        }

        console.log(`Fixed seats for ${email}: planDetails.seats=${correctSeats}, org.maxSeats=${correctSeats}`);
        return { success: true, message: `Updated seats to ${correctSeats}` };
    } catch (error) {
        console.error("Error fixing business account seats:", error);
        return { success: false, error: error.message };
    }
};

// Update conversion funnel metrics
export const updateConversionFunnel = async (email, metric) => {
    if (!database) {
        console.warn("Firebase not initialized");
        return { success: false };
    }

    try {
        const profile = await getHiringManagerProfile(email);
        if (!profile) {
            return { success: false, message: "Profile not found" };
        }

        const currentValue = profile.conversionFunnel?.[metric] ?? 0;
        const profileRef = ref(database, `${DB_PREFIX}hiringManagerProfiles/${profile.id}`);

        await update(profileRef, {
            [`conversionFunnel/${metric}`]: currentValue + 1,
            updatedAt: Date.now()
        });

        console.log(`Updated conversion funnel ${metric} for ${email}. New value: ${currentValue + 1}`);
        return { success: true };
    } catch (error) {
        console.error("Error updating conversion funnel:", error);
        return { success: false, message: error.message };
    }
};

/**
 * Cancel subscription for an organization
 * Only the account owner can cancel the subscription
 * This will:
 * - Set all team members' seatStatus to 'inactive'
 * - Reset credits to 0
 * - Mark subscription as cancelled
 * @param {string} organizationId - Organization ID
 * @param {string} ownerProfileId - The owner's profile ID (to verify ownership)
 */
export const cancelSubscription = async (organizationId, ownerProfileId) => {
    if (!database || !organizationId || !ownerProfileId) {
        throw new Error("Missing required parameters");
    }

    try {
        // First, verify the caller is the owner
        const ownerRef = ref(database, `${DB_PREFIX}hiringManagerProfiles/${ownerProfileId}`);
        const ownerSnapshot = await get(ownerRef);

        if (!ownerSnapshot.exists()) {
            throw new Error("Owner profile not found");
        }

        const ownerProfile = ownerSnapshot.val();
        if (ownerProfile.role !== 'owner') {
            throw new Error("Only the account owner can cancel the subscription");
        }

        if (ownerProfile.organizationId !== organizationId) {
            throw new Error("Organization mismatch");
        }

        // Get all team members in this organization
        const profilesRef = ref(database, `${DB_PREFIX}hiringManagerProfiles`);
        const profilesSnapshot = await get(profilesRef);

        const updates = {};
        const cancelledAt = Date.now();

        if (profilesSnapshot.exists()) {
            profilesSnapshot.forEach((childSnapshot) => {
                const profile = childSnapshot.val();
                if (profile.organizationId === organizationId) {
                    const profileId = childSnapshot.key;

                    // Deactivate seat and reset credits for all members
                    updates[`${DB_PREFIX}hiringManagerProfiles/${profileId}/seatStatus`] = 'inactive';
                    updates[`${DB_PREFIX}hiringManagerProfiles/${profileId}/planDetails/creditsRemaining`] = 0;
                    updates[`${DB_PREFIX}hiringManagerProfiles/${profileId}/planDetails/subscriptionStatus`] = 'cancelled';
                    updates[`${DB_PREFIX}hiringManagerProfiles/${profileId}/planDetails/cancelledAt`] = cancelledAt;
                    updates[`${DB_PREFIX}hiringManagerProfiles/${profileId}/planDetails/autoRenew`] = false;
                    updates[`${DB_PREFIX}hiringManagerProfiles/${profileId}/updatedAt`] = cancelledAt;
                }
            });
        }

        // Also update the organization record if it exists
        updates[`${DB_PREFIX}organizations/${organizationId}/subscriptionStatus`] = 'cancelled';
        updates[`${DB_PREFIX}organizations/${organizationId}/cancelledAt`] = cancelledAt;
        updates[`${DB_PREFIX}organizations/${organizationId}/updatedAt`] = cancelledAt;

        // Apply all updates atomically
        await update(ref(database), updates);

        console.log("Subscription cancelled successfully for organization:", organizationId);
        return { success: true, cancelledAt };
    } catch (error) {
        console.error("Error cancelling subscription:", error);
        throw error;
    }
};